import RoleService from "@/services/api/role";
import {
  createAsyncThunk,
  createSlice,
  type PayloadAction,
} from "@reduxjs/toolkit";

// 👇 Define Role type (adjust fields as per your API response)
export interface Role {
  id: number;
  name: string;
  description?: string;
}

// 👇 Response type from API
export interface GetRolesResponse {
  rows: Role[];
  count: number;
}

// 👇 Slice state type
export interface RoleState {
  totalRoles: number;
  roles: Role[];
  isFetching: boolean;
}

// 👇 Initial state with type
const initialState: RoleState = {
  totalRoles: 0,
  roles: [],
  isFetching: false,
};

// 👇 Async thunk
export const getRoles = createAsyncThunk<
  GetRolesResponse,
  any,
  { rejectValue: string }
>("role/getRoles", async (params, { rejectWithValue }) => {
  try {
    const data = await RoleService.getRoles(params);
    return data;
  } catch (error: any) {
    return rejectWithValue(error.message ?? "Failed to fetch roles");
  }
});

// 👇 Slice
const roleSlice = createSlice({
  name: "role",
  initialState,
  reducers: {
    clear: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getRoles.pending, (state) => {
        state.isFetching = true;
      })
      .addCase(
        getRoles.fulfilled,
        (state, action: PayloadAction<GetRolesResponse>) => {
          state.isFetching = false;
          state.roles = action.payload.rows;
          state.totalRoles = action.payload.count;
        }
      )
      .addCase(getRoles.rejected, (state, action) => {
        state.isFetching = false;
        console.error("Failed to fetch roles:", action.payload);
      });
  },
});

export default roleSlice.reducer;
export const { clear } = roleSlice.actions;
